import { useEffect, useState } from 'react'
import {
  confirmLab,
  fetchLabFileUrl,
  removeLab,
  type InBodyValues,
  type LabDraft,
  type LabValue,
} from '../lib/api'
import { Button, Group } from '../components/ui'
import { IconWarning, IconX } from '../components/icons'

const INBODY_FIELDS: { key: keyof InBodyValues; label: string; unit: string }[] = [
  { key: 'weight_kg', label: 'Вес', unit: 'кг' },
  { key: 'skeletal_muscle_kg', label: 'Скелетные мышцы', unit: 'кг' },
  { key: 'body_fat_kg', label: 'Жировая масса', unit: 'кг' },
  { key: 'body_fat_pct', label: 'Процент жира', unit: '%' },
  { key: 'bmi', label: 'ИМТ', unit: '' },
  { key: 'visceral_fat_level', label: 'Висцеральный жир', unit: 'уровень' },
  { key: 'total_body_water_l', label: 'Общая вода', unit: 'л' },
  { key: 'ecw_tbw', label: 'ECW/TBW', unit: '' },
  { key: 'bmr_kcal', label: 'Основной обмен', unit: 'ккал' },
  { key: 'inbody_score', label: 'Балл InBody', unit: '' },
]

/**
 * Строка формы держит числа СТРОКАМИ, а не числами.
 *
 * Если хранить число, то на полпути к «12.5» состояние проходит через «12.»,
 * Number('12.') даёт 12, React возвращает в поле «12» — точка исчезает под
 * пальцами, и вместо 12.5 сохраняется 125. Для экрана, который существует
 * ровно ради правки цифр, это отменяет весь смысл сверки.
 */
interface DraftRow {
  id: string
  name: string
  value: string
  unit: string
  refLow: string
  refHigh: string
  confidence: LabValue['confidence']
}

const toInput = (value: number | null): string => (value === null ? '' : String(value))

/** Пустая строка — «не знаю», а не ноль: ноль в анализах тоже результат. */
function parseNumber(input: string): number | null {
  const normalized = input.replace(',', '.').trim()
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function toRows(values: LabValue[]): DraftRow[] {
  return values.map((value) => ({
    id: crypto.randomUUID(),
    name: value.name,
    value: toInput(value.value),
    unit: value.unit,
    refLow: toInput(value.ref_low),
    refHigh: toInput(value.ref_high),
    confidence: value.confidence,
  }))
}

function toInbodyDraft(inbody: InBodyValues | null): Record<string, string> {
  return Object.fromEntries(INBODY_FIELDS.map((field) => [field.key, toInput(inbody?.[field.key] ?? null)]))
}

export function LabReview({ draft, onDone }: { draft: LabDraft; onDone: () => void }) {
  const parsed = draft.raw_ai_response
  const isInbody = parsed.kind === 'inbody'

  const [rows, setRows] = useState<DraftRow[]>(() => toRows(parsed.values ?? []))
  const [inbody, setInbody] = useState<Record<string, string>>(() => toInbodyDraft(parsed.inbody))
  const [takenAt, setTakenAt] = useState(draft.taken_at)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!draft.source_file_path) return
    void fetchLabFileUrl(draft.id)
      .then((data) => setSourceUrl(data.url))
      .catch(() => setSourceUrl(null))
  }, [draft.id, draft.source_file_path])

  const updateRow = (id: string, patch: Partial<DraftRow>) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)))
  }

  const removeRow = (id: string) => setRows((current) => current.filter((row) => row.id !== id))

  const save = async () => {
    setError(null)

    if (!/^\d{4}-\d{2}-\d{2}$/.test(takenAt)) {
      setError('Проверь дату забора — нужен формат ГГГГ-ММ-ДД')
      return
    }

    if (isInbody) {
      const values = Object.fromEntries(
        INBODY_FIELDS.map((field) => [field.key, parseNumber(inbody[field.key] ?? '')]),
      ) as unknown as InBodyValues

      if (Object.values(values).every((value) => value === null)) {
        setError('Ни одно поле не заполнено — сохранять нечего')
        return
      }
      await send({ inbody: values })
      return
    }

    const empty = rows.filter((row) => parseNumber(row.value) === null)
    if (empty.length) {
      setError(
        `Без значения нельзя сохранить: ${empty.map((row) => row.name || 'строка без названия').join(', ')}. Впиши число или убери строку.`,
      )
      return
    }

    const nameless = rows.filter((row) => !row.name.trim())
    if (nameless.length) {
      setError('У каждого показателя должно быть название')
      return
    }

    await send({
      values: rows.map((row) => ({
        name: row.name.trim(),
        value: parseNumber(row.value) as number,
        unit: row.unit.trim(),
        ref_low: parseNumber(row.refLow),
        ref_high: parseNumber(row.refHigh),
        confidence: row.confidence,
      })),
    })
  }

  const send = async (payload: { values?: LabValue[]; inbody?: InBodyValues }) => {
    setBusy(true)
    try {
      const result = await confirmLab(draft.id, { ...payload, takenAt })
      if (result.rejected.length) {
        setError(`Не принял: ${result.rejected.join('; ')}. Проверь эти строки.`)
        setBusy(false)
        return
      }
      onDone()
    } catch (cause) {
      setError((cause as Error).message)
      setBusy(false)
    }
  }

  const discard = async () => {
    setBusy(true)
    try {
      await removeLab(draft.id)
      onDone()
    } catch (cause) {
      setError((cause as Error).message)
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 p-4 pb-nav">
      <Group caption="Сверь с бланком">
        <p className="text-sm text-ink-2">
          Распознавание ошибается чаще всего на колонке референса — она стоит вплотную к значению.
          Пока не подтвердишь, ничего не сохраняется.
        </p>
        <div className="mt-3 flex items-center gap-3 text-sm">
          <label className="flex-1" htmlFor="taken-at">
            Дата забора
          </label>
          <input
            id="taken-at"
            type="date"
            className="numerals rounded-lg border border-ink-separator bg-ink-surface2 px-2 py-1.5 text-sm"
            value={takenAt}
            onChange={(event) => setTakenAt(event.target.value)}
          />
        </div>
        {draft.lab_name && <div className="mt-1 text-xs text-ink-2">{draft.lab_name}</div>}
      </Group>

      {parsed.warnings.length > 0 && (
        <div className="rounded-xl border border-sys-orange/40 bg-sys-orange/10 p-3 text-xs text-sys-orange">
          {parsed.warnings.map((warning) => (
            <div key={warning} className="flex items-start gap-2"><IconWarning size={14} className="mt-0.5 shrink-0" />{warning}</div>
          ))}
        </div>
      )}

      {sourceUrl && <SourcePreview url={sourceUrl} />}

      {isInbody ? (
        <Group caption="InBody">
          {INBODY_FIELDS.map((field) => (
            <div
              key={field.key}
              className="flex items-center gap-3 border-b border-ink-separator py-2 last:border-none"
            >
              <label className="flex-1 text-sm" htmlFor={`inbody-${field.key}`}>
                {field.label}
                {field.unit && <span className="ml-1 text-ink-2">({field.unit})</span>}
              </label>
              <input
                id={`inbody-${field.key}`}
                inputMode="decimal"
                className="numerals w-24 rounded-lg border border-ink-separator bg-ink-surface2 px-2 py-1.5 text-right text-sm"
                value={inbody[field.key] ?? ''}
                onChange={(event) =>
                  setInbody((current) => ({ ...current, [field.key]: event.target.value }))
                }
              />
            </div>
          ))}
        </Group>
      ) : (
        <Group caption={`Показатели · ${rows.length}`}>
          {rows.length === 0 && <p className="text-sm text-ink-2">Ничего не распозналось.</p>}
          {rows.map((row) => (
            <ValueRow
              key={row.id}
              row={row}
              onChange={(patch) => updateRow(row.id, patch)}
              onRemove={() => removeRow(row.id)}
            />
          ))}
        </Group>
      )}

      {error && (
        <p className="rounded-xl border border-sys-red/40 bg-sys-red/10 p-3 text-xs text-sys-red">
          {error}
        </p>
      )}

      <p className="text-[11px] leading-relaxed text-ink-2">
        Это не медицинская консультация. Трактовать результаты и назначать что-либо может только
        врач.
      </p>

      <div className="flex gap-2">
        <Button variant="filled" onClick={save} disabled={busy}>
          {busy ? 'Сохраняю…' : 'Подтвердить и сохранить'}
        </Button>
        <Button onClick={discard} disabled={busy}>
          Удалить
        </Button>
      </div>
    </div>
  )
}

function ValueRow({
  row,
  onChange,
  onRemove,
}: {
  row: DraftRow
  onChange: (patch: Partial<DraftRow>) => void
  onRemove: () => void
}) {
  const doubtful = row.confidence === 'low'

  return (
    <div
      className={`border-b border-ink-separator py-2.5 last:border-none ${
        doubtful ? '-mx-2 rounded-lg bg-sys-orange/10 px-2' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <input
          className="min-w-0 flex-1 rounded-lg border border-ink-separator bg-ink-surface2 px-2 py-1.5 text-sm"
          value={row.name}
          onChange={(event) => onChange({ name: event.target.value })}
        />
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 px-1 text-ink-2"
          aria-label="Убрать показатель"
        >
          <IconX size={14} />
        </button>
      </div>

      <div className="mt-1.5 flex items-center gap-1.5">
        <input
          inputMode="decimal"
          className="numerals w-20 rounded-lg border border-ink-separator bg-ink-surface2 px-2 py-1.5 text-right text-sm"
          value={row.value}
          onChange={(event) => onChange({ value: event.target.value })}
        />
        <input
          className="w-20 rounded-lg border border-ink-separator bg-ink-surface2 px-2 py-1.5 text-sm"
          value={row.unit}
          onChange={(event) => onChange({ unit: event.target.value })}
        />
        <span className="text-xs text-ink-2">норма</span>
        <input
          inputMode="decimal"
          className="numerals w-14 rounded-lg border border-ink-separator bg-ink-surface2 px-1.5 py-1.5 text-right text-sm"
          value={row.refLow}
          onChange={(event) => onChange({ refLow: event.target.value })}
        />
        <span className="text-ink-2">–</span>
        <input
          inputMode="decimal"
          className="numerals w-14 rounded-lg border border-ink-separator bg-ink-surface2 px-1.5 py-1.5 text-right text-sm"
          value={row.refHigh}
          onChange={(event) => onChange({ refHigh: event.target.value })}
        />
      </div>

      {doubtful && <p className="mt-1 text-[11px] text-sys-orange">Прочиталось плохо — проверь</p>}
    </div>
  )
}

/** PDF в WebView надёжнее открыть снаружи, картинку — показать на месте. */
function SourcePreview({ url }: { url: string }) {
  if (url.includes('.pdf')) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="rounded-xl border border-ink-separator bg-ink-surface p-3 text-center text-sm text-exercise"
      >
        Открыть исходный PDF
      </a>
    )
  }
  return (
    <img
      src={url}
      alt="Бланк анализа"
      className="w-full rounded-2xl border border-ink-separator"
      loading="lazy"
    />
  )
}
